import React from 'react';
import 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

import { createContext } from '../hooks/useContext';
import { useCookieConsentLanguageOptions } from '../hooks/useCookieConsentLanguageOptions';
import Logger from '../utils/Logger';
import { useAuth } from './AuthProvider';
import { useGoogleAnalytics } from './GoogleAnalyticsProvider';

export interface CookieConsentContext {
  readonly isConsentCookieDefined: boolean;
  readonly action: {
    decline(category: string | string[]): void;
  };
}

export const [, useCookieConsent, cookieConsentCtx] =
  createContext<CookieConsentContext>();

function checkCookieExists(cookieName: string) {
  return document.cookie
    .split(';')
    .some((item) => item.trim().startsWith(`${cookieName}=`));
}

const VANILLA_COOKIE_CONSENT_COOKIE_NAME = 'cc_cookie' as const;

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const authCtx = useAuth();
  const langOptions = useCookieConsentLanguageOptions();
  const { actions: googleAnalyticsActions } = useGoogleAnalytics();
  const [isConsentCookieDefined, setIsConsentCookieDefined] = React.useState(
    !!checkCookieExists(VANILLA_COOKIE_CONSENT_COOKIE_NAME)
  );
  const cookieConsentInstance = React.useRef<CookieConsent>();

  React.useEffect(() => {
    if (cookieConsentInstance.current || authCtx.waitingForAuthStatus) {
      return;
    }

    cookieConsentInstance.current = window.initCookieConsent();
    cookieConsentInstance.current.run({
      ...langOptions,
      autoclear_cookies: true,
      force_consent: true,
      page_scripts: true,
      onAccept: () => {
        googleAnalyticsActions.initGoogleAnalytics();
        setIsConsentCookieDefined(true);
      },
      onChange: () => {
        setIsConsentCookieDefined(true);
      },
    });
  }, [
    authCtx.waitingForAuthStatus,
    googleAnalyticsActions,
    langOptions,
    setIsConsentCookieDefined,
    cookieConsentInstance,
  ]);

  const decline = React.useCallback((exclusions: string[]) => {
    const cc = cookieConsentInstance.current;
    if (!cc) {
      Logger.error('cookieConsent not initialized');
      return;
    }
    const userPreferences = cc.getUserPreferences();
    cc.accept(userPreferences.accepted_categories, exclusions);
    setTimeout(() => window.location.reload(), 0);
  }, []);

  const action = React.useMemo(() => {
    return {
      decline,
    };
  }, [decline]);

  const context = React.useMemo(() => {
    return {
      isConsentCookieDefined,
      action,
    };
  }, [action, isConsentCookieDefined]);

  return <cookieConsentCtx.Provider value={context} children={children} />;
};
